/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AddEditFallbackPageComponent
} from '../../../pages/maintenance/fallbackPages/addEdit/addEditFallbackPage.component';
import { BaseComponent } from '../../../../../core/components';
import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { FallbackPagesApiService } from '../../http';
import { FormGroup } from '@angular/forms';
import { LogService, RbacService, UtilsService } from '../../../../../core/services';
import { WizardService } from '../../services/components';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-fallback-server-options',
    templateUrl: './fallbackServerOptions.component.html',
    styleUrls: ['../../../../../../assets/shared/scss/embedded.scss']
})

export class FallbackServerOptionsComponent extends BaseComponent {
    /**
     * Reference to add/edit certs container.
     */
    @ViewChild('addContainer', { read: ViewContainerRef, static: true })
    public addContainer: any;

    /**
     * Set the fallback config from the parent component.
     * @param config
     */
    @Input() set fallbackConfig(config: { fallbackServerId: string, fallbackServerForwardingMode: string }) {
        this._fallbackConfig = config;
        this.showFallbackForwardingMode = config.fallbackServerId && config.fallbackServerId !== 'none';
    }

    /**
     * Set form group from parent.
     * @param formGroup
     */
    @Input() set formGroup(formGroup: FormGroup) {
        this._formGroup = formGroup;
    }

    /**
     * Pass the fallback server config back up to the parent.
     */
    @Output() public updateFallbackConfig: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Tells the parent that the addEditPage is currently active/visible.
     */
    @Output() public addPageActive: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown = {
        fallbackServers: [],
        forwardingModes: [],
    };

    /**
     * Store visibility state of add fallback page.
     */
    public isAddFallbackPageVisible: boolean = false;

    /**
     * Store permissions' lookup.
     */
    public permissionsLookup: any = {};

    /**
     * Show fallback forwarding mode.
     */
    public showFallbackForwardingMode: boolean = false;

    /**
     * Store the list of fallback servers.
     * @private
     */
    private fallBackServers: any[] = [];

    /**
     * Store component reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    /**
     * Store fallback server config.
     * @private
     */
    private _fallbackConfig: any;

    /**
     * Variable for formgroup setter/getter
     * @private
     */
    private _formGroup: FormGroup;

    constructor(
        private cdr: ChangeDetectorRef,
        private fallbackPagesApiService: FallbackPagesApiService,
        private logService: LogService,
        private rbacService: RbacService,
        private utilsService: UtilsService,
        private wizardService: WizardService
    ) {
        super();
        this.setUpPageData();
        this.setupModeDropdown();
        this.registerButtons();
    }

    /**
     * N.B This is required for validation purposes.
     */
    get formGroup() {
        return this._formGroup;
    }

    get fallbackConfig() {
        return this._fallbackConfig;
    }

    /**
     * Update fallback server id.
     * @param serverId
     */
    public updateFallbackServerId(serverId: string): void {
        this.fallbackConfig.fallbackServerId = serverId;
        this.showFallbackForwardingMode = serverId !== 'none';
        this.updateFallbackConfig.emit(this.fallbackConfig);
    }

    /**
     * Update forwarding mode.
     * @param mode
     */
    public updateForwardingMode(mode: string): void {
        this.fallbackConfig.fallbackServerForwardingMode = mode;
        this.updateFallbackConfig.emit(this.fallbackConfig);
    }

    /**
     * Create instance of toggle component.
     */
    public setUpFallbackPageComponent(): void {
        const embeddedComponent = this.addContainer.createComponent(AddEditFallbackPageComponent);
        embeddedComponent.instance.callBack = this.cancelAddFallbackServerView.bind(this);
        embeddedComponent.instance.embedded = true;
        this.isAddFallbackPageVisible = true;
        this.addPageActive.emit(true);
        this.wizardService.disableNavigation = true;

        this.componentReference = embeddedComponent;
        this.cdr.detectChanges();
    }

    /**
     * Cancel cipher list view.
     * @private
     */
    private cancelAddFallbackServerView(): void {
        this.componentReference.destroy();
        this.isAddFallbackPageVisible = false;
        this.addPageActive.emit(false);
        this.wizardService.disableNavigation = false;
        this.setUpPageData();
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.fallbackPagesApiService.getAll()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any[]): void => {
                    this.fallBackServers = data;
                    this.dropdown.fallbackServers = this.getFallbackServers();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('FallbackServerOptionsComponent',
                        'Failed to fetch fallback servers', error.message);
                }
            });
    }

    /**
     * Set up forwarding mode dropdown.
     * @private
     */
    private setupModeDropdown(): void {
        this.dropdown.forwardingModes = [
            <DropdownModel>{
                key: 'directRouting',
                value: 'Direct Routing'
            },
            <DropdownModel>{
                key: 'destinationNat',
                value: 'DNAT'
            },
            <DropdownModel>{
                key: 'sourceNat',
                value: 'SNAT'
            },
            <DropdownModel>{
                key: 'tunnelling',
                value: 'Tunneling'
            }
        ];
    }

    /**
     * Get the fallback servers.
     */
    private getFallbackServers(): DropdownModel[] {
        const fallbackData: DropdownModel[] = this.utilsService.convertArrayToDropDown(this.fallBackServers,
            'id', 'name'
        );
        fallbackData.unshift({ key: 'none', value: 'none' });

        return fallbackData;
    }

    /**
     * Register buttons.
     * @private
     */
    private registerButtons(): void {
        const buttonReferences = {
            'fallback-server-add-fallback': [{ path: '/fallback', methods: ['GET', 'POST'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReferences);
    }
}
